/**
 * MUI Theme Configuration
 *
 * Creates Material-UI theme based on our custom theme system.
 * Supports all 4 themes: Dark, Light, High Contrast, Industrial
 */

import { createTheme } from '@mui/material/styles';
import { getTheme, isDarkTheme } from '../constants/theme';

// Shared typography config (industrial/monospace aesthetic)
export const sharedTypography = {
  fontFamily: "'IBM Plex Mono', monospace",
  h1: {
    fontFamily: "'Oswald', sans-serif",
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '-0.025em',
  },
  h2: {
    fontFamily: "'Oswald', sans-serif",
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '-0.025em',
  },
  h3: {
    fontFamily: "'Oswald', sans-serif",
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '-0.025em',
  },
  h4: {
    fontFamily: "'Oswald', sans-serif",
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '-0.025em',
  },
  h5: {
    fontFamily: "'Oswald', sans-serif",
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '-0.025em',
  },
  h6: {
    fontFamily: "'Oswald', sans-serif",
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '-0.025em',
  },
  subtitle1: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontWeight: 500,
  },
  subtitle2: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontWeight: 500,
  },
  body1: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontWeight: 400,
  },
  body2: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontWeight: 400,
  },
  button: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
  caption: {
    fontFamily: "'IBM Plex Mono', monospace",
    fontWeight: 400,
  },
  overline: {
    fontFamily: "'Oswald', sans-serif",
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
  },
};

/**
 * Create MUI theme from our custom theme ID
 * @param {string} themeId - Theme identifier (dark, light, highContrast, industrial)
 * @returns {object} MUI Theme object
 */
export const createMuiTheme = (themeId) => {
  const customTheme = getTheme(themeId);
  const colors = customTheme.colors;
  const isDark = isDarkTheme(themeId);
  const mode = isDark ? 'dark' : 'light';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: colors.primary.main,
        light: colors.primary.light,
        dark: colors.primary.dark,
        contrastText: colors.primary.contrastText,
      },
      secondary: {
        main: colors.secondary.main,
        light: colors.secondary.light,
        dark: colors.secondary.dark,
      },
      background: {
        default: colors.background,
        paper: colors.surface,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
        disabled: colors.text.muted,
      },
      divider: colors.border.default,
      action: {
        active: colors.success.main, // Green for active states
        hover: colors.success.subtle,
        selected: colors.success.subtleHover,
        disabled: colors.text.muted,
        disabledBackground: colors.surfaceElevated,
      },
      error: {
        main: colors.error.main,
        light: colors.error.light,
        dark: colors.error.dark,
      },
      warning: {
        main: colors.warning.main,
        light: colors.warning.light,
        dark: colors.warning.dark,
      },
      info: {
        main: colors.info.main,
        light: colors.info.light,
        dark: colors.info.dark,
      },
      success: {
        main: colors.success.main,
        light: colors.success.light,
        dark: colors.success.dark,
      },
    },
    typography: sharedTypography,
    shape: {
      borderRadius: 0, // Industrial aesthetic - sharp corners
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: colors.background,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            textTransform: 'uppercase',
            fontWeight: 600,
            letterSpacing: '0.1em',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          containedPrimary: {
            backgroundColor: colors.primary.main,
            color: colors.primary.contrastText,
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
          },
          outlined: {
            borderColor: colors.border.default,
            '&:hover': {
              borderColor: colors.primary.main,
              backgroundColor: colors.hover.background,
            },
          },
          outlinedPrimary: {
            borderColor: colors.border.default,
            color: colors.text.secondary,
            '&:hover': {
              borderColor: colors.primary.main,
              color: colors.primary.main,
              backgroundColor: colors.hover.background,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            borderRadius: 0,
          },
          elevation1: {
            boxShadow: 'none',
            border: `1px solid ${colors.border.default}`,
          },
          elevation2: {
            boxShadow: 'none',
            border: `1px solid ${colors.border.default}`,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.background,
            backgroundImage: 'none',
            boxShadow: 'none',
            borderBottom: `1px solid ${colors.border.default}`,
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.background,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              backgroundColor: colors.background,
              '& fieldset': {
                borderColor: colors.border.light,
              },
              '&:hover fieldset': {
                borderColor: colors.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.border.focus,
                borderWidth: '2px',
              },
            },
            '& .MuiInputBase-input': {
              fontFamily: "'IBM Plex Mono', monospace",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            '& fieldset': {
              borderColor: colors.border.light,
            },
            '&:hover fieldset': {
              borderColor: colors.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.border.focus,
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: colors.success.subtle,
              borderLeft: `4px solid ${colors.success.main}`,
              '&:hover': {
                backgroundColor: colors.success.subtleHover,
              },
            },
            '&:hover': {
              backgroundColor: colors.hover.background,
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              backgroundColor: colors.success.subtle,
              borderLeft: `4px solid ${colors.success.main}`,
              '&:hover': {
                backgroundColor: colors.success.subtleHover,
              },
            },
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border.default}`,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: "'Oswald', sans-serif",
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '-0.025em',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border.default}`,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: "'IBM Plex Mono', monospace",
            '&:hover': {
              backgroundColor: colors.success.subtle,
            },
            '&.Mui-selected': {
              backgroundColor: colors.success.subtleHover,
              '&:hover': {
                backgroundColor: colors.success.subtleHover,
              },
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            backgroundColor: colors.surface,
          },
          indicator: {
            backgroundColor: colors.success.main, // Green indicator
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontFamily: "'IBM Plex Mono', monospace",
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            '&.Mui-selected': {
              color: colors.success.main, // Green when selected
            },
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
          standardError: {
            backgroundColor: colors.error.subtle,
            borderLeft: `4px solid ${colors.error.main}`,
          },
          standardWarning: {
            backgroundColor: colors.warning.subtle,
            borderLeft: `4px solid ${colors.warning.main}`,
          },
          standardInfo: {
            backgroundColor: colors.info.subtle,
            borderLeft: `4px solid ${colors.info.main}`,
          },
          standardSuccess: {
            backgroundColor: colors.success.subtle,
            borderLeft: `4px solid ${colors.success.main}`,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: colors.border.default,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            '&:hover': {
              backgroundColor: colors.success.subtle,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
        },
      },
      MuiFab: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          colorPrimary: {
            color: colors.success.main, // Green spinner
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: 0,
          },
          colorPrimary: {
            backgroundColor: colors.surfaceElevated,
          },
          barColorPrimary: {
            backgroundColor: colors.success.main,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': {
              color: colors.success.main,
              '& + .MuiSwitch-track': {
                backgroundColor: colors.success.main,
              },
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: colors.success.main,
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            '&.Mui-checked': {
              color: colors.success.main,
            },
          },
        },
      },
      MuiSlider: {
        styleOverrides: {
          root: {
            color: colors.success.main,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: colors.surfaceElevated,
            color: colors.text.primary,
            border: `1px solid ${colors.border.default}`,
            borderRadius: 0,
            fontFamily: "'IBM Plex Mono', monospace",
          },
        },
      },
      MuiSnackbar: {
        styleOverrides: {
          root: {
            '& .MuiPaper-root': {
              borderRadius: 0,
            },
          },
        },
      },
    },
  });
};

export default createMuiTheme;
